import * as React from 'react';
import Lazy from 'lazy.js';
import propz from 'propz';
import Moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { SchoolStudentClash } from 'Src/models/schoolStudent';
import { SchoolEvent } from 'Src/models/event';
import { Team } from 'Src/models/team';
import { Loader } from 'Src/components/Loader/Loader';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ClashEventsModal } from 'Src/components/ClashEventsModal/ClashEventsModal';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { ColumnDefinition, getOrder, isFilterExist2, TABLE_SORT_DIRECTION } from 'Src/helpers/table/table';
import { getAge, getAgeGroup, getAllowedGender, getGender } from 'Src/helpers/accessor/accessor';
import { getTwoPanelEditorFilters } from 'Src/helpers/twoPanelEditor/twoPanelEditor';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getSchoolEvent } from 'Src/helpers/service/admin/event';
import { getSchoolStudentsWithClashesCount } from 'Src/helpers/service/admin/schoolTeamPlayers';
import {
  getEventClash,
  getClashEventDisplayTitle,
  getStudentsMethod,
  getPrototypeTeamPlayersMethod,
  isHousesEvent,
  isInterSchoolsEvent
} from 'Src/helpers/event/event';
import { getNameAndAges, searchFunctionSchoolTeams } from 'Src/helpers/autocomplete/autocomplete';
import { getUserGenderArrayConvertedFromEventGender } from 'Src/helpers/tournament/tournament';
import {
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForHouses
} from 'Src/helpers/table/select';
import { getColorClashes } from 'Src/helpers/cell/cell';
import { FILTER_TYPE, LIMIT, DEFAULT_LIMIT } from 'Src/consts/table';
import { TEAM_TYPE } from 'Src/consts/team';
import { AGE_GROUPS } from 'Src/consts/school';
import { SchoolEventFromForm } from '../SchoolEventManager';
import { SchoolEventParticipantsPageHelp } from '../SchoolEventParticipantsPageHelp';
import BPromise from 'bluebird';
import './SchoolEventAddExistingTeam.scss';

// ─── Column definitions ───────────────────────────────────────────────────────

const ITEMS_ACCUMULATOR_COLUMNS: ColumnDefinition[] = [
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.NONE, isSort: true },
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.NONE, isSort: true },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  { text: 'Age', field: 'ages', isSort: false, type: FILTER_TYPE.NONE, accessor: getAge, isMobileHidden: true },
  { text: 'Gender', field: 'gender', isSort: false, type: FILTER_TYPE.NONE, accessor: getGender, isMobileHidden: true }
];

const ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  { text: 'Clashes', field: 'clashes', cell: getColorClashes, type: FILTER_TYPE.NONE, isSort: false },
  ...ITEMS_ACCUMULATOR_COLUMNS
];

const ITEMS_COLUMNS: ColumnDefinition[] = [
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.TEXT, isSort: true },
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.TEXT, isSort: true },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  { text: 'Age', field: 'ages', isSort: false, type: FILTER_TYPE.MULTISELECT, accessor: getAge, isMobileHidden: true },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender,
    isMobileHidden: true
  }
];

const ITEMS_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  { text: 'Clashes', field: 'clashes', cell: getColorClashes, type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.TEXT, isSort: true },
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.TEXT, isSort: true },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  { text: 'Age', field: 'ages', isSort: false, type: FILTER_TYPE.MULTISELECT, accessor: getAge, isMobileHidden: true },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender,
    isMobileHidden: true
  }
];

const DEFAULT_TEAM_PLAYERS_ORDER = 'firstName ASC';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Item {
  id: string;
  permissionId: string;
  form: any;
  house: any;
  gender: string;
  firstName: string;
  lastName: string;
  clashes: any;
}

interface Props {
  user: AppUser;
  currentEvent: SchoolEventFromForm;
  blackListTeamIds: string[];
  onAddExistingTeamClick: (team: any) => void;
  onAddNewTeamClick: (team: any) => void;
  onSaveClick: (team: any, isCreateAnotherEvent: boolean) => void;
  isSaveButtonDisabled: boolean;
  isAddTeamButtonDisabled: boolean;
  minTeams: number;
  maxTeams: number;
  teams: Team[];
  goBack: () => void;
  onRemoveTeamClick: (teamIndex: number) => void;
  participantsModeSwitch?: React.ReactNode;
}

interface State {
  forms: SchoolForm[];
  houses: SchoolHouse[];
  isLoading: boolean;
  isItemsLoading: boolean;
  isError: boolean;
  itemsSearch: string;

  selectedTeam: Team;

  items: Item[];
  itemsCount: number;
  itemsSelected: Item[];
  itemsFilters: any;
  isShowItemsFilter: boolean;
  isItemsFiltered: boolean;
  isSelectAllItemsChecked: boolean;
  sortItemsDirection: TABLE_SORT_DIRECTION;
  sortItemsColumnsName: string;
  itemCurrentPage: number;

  itemsAccumulator: Item[];
  itemsAccumulatorInitial: Item[];
  itemsAccumulatorCount: number;
  itemsAccumulatorSelected: Item[];
  itemsAccumulatorFilters: any;
  isShowItemsAccumulatorFilter: boolean;
  isItemsAccumulatorFiltered: boolean;
  isSelectAllItemsAccumulatorChecked: boolean;
  sortItemsAccumulatorDirection: TABLE_SORT_DIRECTION;
  sortItemsAccumulatorColumnsName: string;

  itemsAddBuffer: Item[];
  itemsRemoveBuffer: Item[];

  playerEventsModalText: string;
  isPlayerEventsModalOpen: boolean;
  houseId: string;

  itemsHiddenFields: Set<string>;
  itemsAccumulatorHiddenFields: Set<string>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export class SchoolEventAddExistingTeam extends React.Component<Props, State> {
  private scrollPositionBeforeLoading: number | null = null;
  private itemsSearchRequestId = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      forms: [],
      houses: [],
      isLoading: false,
      isItemsLoading: false,
      isError: false,
      itemsSearch: '',
      selectedTeam: undefined,

      items: [],
      itemsCount: 0,
      itemsSelected: [],
      itemsFilters: {},
      isShowItemsFilter: false,
      isItemsFiltered: false,
      isSelectAllItemsChecked: false,
      sortItemsDirection: '',
      sortItemsColumnsName: '',
      itemCurrentPage: 1,

      itemsAccumulator: [],
      itemsAccumulatorInitial: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],
      itemsAccumulatorFilters: {},
      isShowItemsAccumulatorFilter: false,
      isItemsAccumulatorFiltered: false,
      isSelectAllItemsAccumulatorChecked: false,
      sortItemsAccumulatorDirection: '',
      sortItemsAccumulatorColumnsName: '',

      itemsAddBuffer: [],
      itemsRemoveBuffer: [],

      playerEventsModalText: '',
      isPlayerEventsModalOpen: false,
      houseId: isHousesEvent(props.currentEvent) ? propz.get(props.currentEvent, ['houses', 0, 'id'], '') : '',

      itemsHiddenFields: this.loadHiddenFields('addExistingTeam_items'),
      itemsAccumulatorHiddenFields: this.loadHiddenFields('addExistingTeam_accumulator')
    };
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  componentDidMount() {
    const { user, currentEvent } = this.props;
    const itemsFilters = getTwoPanelEditorFilters(ITEMS_COLUMNS);
    const isShowItemsFilter = isFilterExist2(itemsFilters);
    const defaultItemsFilter = this.getDefaultItemsFilter();

    this.setState({ isLoading: true, itemsSearch: '' });
    Promise.all([getAllForms(user), getAllHouses(user)]).then(([forms, houses]) => {
      this.setState({ forms, houses, itemsFilters: defaultItemsFilter, isShowItemsFilter, isLoading: false });
    });
  }

  // ─── localStorage ─────────────────────────────────────────────────────────

  private static ALWAYS_VISIBLE = new Set(['firstName', 'lastName', 'clashes']);

  private get storageUserId(): string {
    return this.props.user?.userId || 'anonymous';
  }

  private loadHiddenFields(key: string): Set<string> {
    const userId = this.props.user?.userId || 'anonymous';
    try {
      const raw = localStorage.getItem(`columnVisibility__${userId}__${key}`);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set<string>(parsed);
    } catch {}
    return new Set();
  }

  private saveHiddenFields(key: string, fields: Set<string>): void {
    try {
      localStorage.setItem(`columnVisibility__${this.storageUserId}__${key}`, JSON.stringify(Array.from(fields)));
    } catch {}
  }

  isColumnToggleable = (field: string) => !SchoolEventAddExistingTeam.ALWAYS_VISIBLE.has(field);

  onToggleItemsColumn = (field: string) => {
    if (SchoolEventAddExistingTeam.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('addExistingTeam_items', next);
      return { itemsHiddenFields: next };
    });
  };

  onToggleItemsAccumulatorColumn = (field: string) => {
    if (SchoolEventAddExistingTeam.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsAccumulatorHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('addExistingTeam_accumulator', next);
      return { itemsAccumulatorHiddenFields: next };
    });
  };

  // ─── Filter helpers ───────────────────────────────────────────────────────

  getDefaultItemsFilter(houseId = this.state.houseId) {
    const { currentEvent } = this.props;
    const { ages, gender } = currentEvent;
    const genderConverted = getUserGenderArrayConvertedFromEventGender(gender);
    return {
      firstName: '',
      lastName: '',
      gender: genderConverted,
      forms: [],
      ages,
      houses: houseId ? [houseId] : []
    };
  }

  // Note: currentEvent.startTime/endTime are Date objects (not strings), same as SchoolEventAddNewTeam
  getItemsFilter(page: number, accumInitial: Item[], addBuffer: Item[], isAllItems = false) {
    const { itemsFilters, forms, houseId, itemsSearch } = this.state;
    const { currentEvent, teams } = this.props;
    const { startTime, endTime } = currentEvent;

    const {
      gender: fGender,
      ages: fAges = [],
      firstName: fFirst = '',
      lastName: fLast = '',
      forms: fForms = [],
      houses: fHouses = []
    } = itemsFilters;

    let where: any = { gender: { $in: fGender } };

    // Exclude players already in other teams added in this wizard session
    const playerUserIds = Lazy(teams)
      .map((team: any) => team.players || [])
      .flatten()
      .map((player: any) => ({ id: player.userId }))
      .toArray();

    const allItems = [...accumInitial, ...addBuffer, ...playerUserIds];
    const ninIds = Lazy(allItems)
      .map((x: any) => x.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);

    if (fForms.length > 0) {
      propz.set(where, ['formId', '$in'], fForms);
    } else {
      const ages = fAges.length > 0 ? fAges : AGE_GROUPS.ENGLISH.map((_: any, i: number) => i);
      const formIds = forms.filter(f => ages.some((a: number) => Number(f.age) === Number(a))).map(f => f.id);
      if (formIds.length > 0) propz.set(where, ['formId', '$in'], formIds);
    }

    if (isHousesEvent(currentEvent) && houseId) propz.set(where, ['houseId'], houseId);

    if (fHouses.length > 0) propz.set(where, ['houseId', '$in'], fHouses);

    const and: any[] = [];
    if (fFirst.length > 0) and.push({ firstName: { like: fFirst, options: 'i' } });
    if (fLast.length > 0) and.push({ lastName: { like: fLast, options: 'i' } });
    if (itemsSearch.trim().length > 0) {
      const searchValue = itemsSearch.trim();
      and.push({
        $or: [{ firstName: { like: searchValue, options: 'i' } }, { lastName: { like: searchValue, options: 'i' } }]
      });
    }
    if (and.length > 0) propz.set(where, ['$and'], and);

    // startTime is Date here (SchoolEventFromForm), use .getHours() directly
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTime.getHours() - 1);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTime.getHours() + 1);

    propz.set(
      where,
      ['intervalClashStartDate'],
      Moment(startTimeCopy)
        .utc()
        .format()
    );
    propz.set(
      where,
      ['intervalClashEndDate'],
      Moment(endTimeCopy)
        .utc()
        .format()
    );
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    return {
      where,
      limit: isAllItems ? DEFAULT_LIMIT : LIMIT,
      skip: isAllItems ? 0 : (page - 1) * LIMIT,
      order: DEFAULT_TEAM_PLAYERS_ORDER
    };
  }

  // ─── Normalize ────────────────────────────────────────────────────────────

  normalizePlayers(players: any[]): Item[] {
    return players.map(p => ({
      permissionId: p.permissionId,
      form: p.form,
      house: p.house,
      gender: p.gender,
      firstName: p.firstName,
      lastName: p.lastName,
      clashes: p.clashes || { status: 'NONE', fullClashEvents: [], intervalClashEvents: [] },
      id: p.userId || p.id
    }));
  }

  normalizeStudents(students: SchoolStudentClash[]): Item[] {
    return students.map(s => ({
      permissionId: s.permissionId,
      form: s.form,
      house: s.house,
      gender: s.gender,
      firstName: s.firstName,
      lastName: s.lastName,
      clashes: s.clashes,
      id: s.id
    }));
  }

  fetchStudents(page: number, accumInitial: Item[], addBuffer: Item[], isAllItems = false) {
    const { user } = this.props;
    const filter = this.getItemsFilter(page, accumInitial, addBuffer, isAllItems);
    const method = getStudentsMethod(user);
    return Promise.all([method(user, filter), getSchoolStudentsWithClashesCount(user, filter)]);
  }

  // ─── Team search ──────────────────────────────────────────────────────────

  getSchoolTeams = (text: string): Promise<Team[]> => {
    const { user, currentEvent, blackListTeamIds, teams } = this.props;
    const { houseId } = this.state;
    const { ages, gender, sportId, houses } = currentEvent;
    const isTeamsExist = teams.length > 0;
    const housesFiltered = houses.filter(house => teams.every(team => team.houseId !== house.id));

    return searchFunctionSchoolTeams(user, {
      teamIds: blackListTeamIds.filter(id => typeof id !== 'undefined'),
      text,
      ages,
      gender,
      sportId,
      houses: isHousesEvent(currentEvent)
        ? houses.filter(house => house.id === houseId)
        : isTeamsExist
        ? housesFiltered
        : houses
    });
  };

  onHouseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const houseId = event.target.value;
    this.setState({
      houseId,
      itemsSearch: '',
      selectedTeam: undefined,
      isError: false,
      items: [],
      itemsCount: 0,
      itemsSelected: [],
      itemsFilters: this.getDefaultItemsFilter(houseId),
      itemsAccumulator: [],
      itemsAccumulatorInitial: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],
      itemsAddBuffer: [],
      itemsRemoveBuffer: []
    });
  };

  // ─── Team selection ───────────────────────────────────────────────────────

  onSelectTeam = (team: Team) => {
    const { user } = this.props;
    const { currentEvent } = this.props;
    const { startTime, endTime } = currentEvent;

    this.setState({ isLoading: true, itemsSearch: '' });

    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTime.getHours() - 1);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTime.getHours() + 1);

    const clashWhere: any = {};
    propz.set(
      clashWhere,
      ['intervalClashStartDate'],
      Moment(startTimeCopy)
        .utc()
        .format()
    );
    propz.set(
      clashWhere,
      ['intervalClashEndDate'],
      Moment(endTimeCopy)
        .utc()
        .format()
    );
    propz.set(clashWhere, ['fullClashStartDate'], startTime);
    propz.set(clashWhere, ['fullClashEndDate'], endTime);

    const accumFilters = getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS);

    getPrototypeTeamPlayersMethod(user)(user, team.id, { where: clashWhere }).then(players => {
      const normalized = this.normalizePlayers(players);
      this.fetchStudents(1, normalized, []).then(([students, studentsCount]) => {
        this.setState({
          selectedTeam: team,
          itemsAccumulator: normalized,
          itemsAccumulatorInitial: normalized,
          itemsAccumulatorCount: normalized.length,
          itemsAccumulatorFilters: accumFilters,
          isShowItemsAccumulatorFilter: isFilterExist2(accumFilters),
          isItemsAccumulatorFiltered: false,
          itemsAddBuffer: [],
          itemsRemoveBuffer: [],
          items: this.normalizeStudents(students),
          itemsCount: studentsCount.count,
          itemCurrentPage: 1,
          itemsSelected: [],
          itemsAccumulatorSelected: [],
          isSelectAllItemsChecked: false,
          isSelectAllItemsAccumulatorChecked: false,
          isLoading: false,
          isError: false
        });
      });
    });
  };

  renderTeamOption = (team: Team): React.ReactNode => {
    const ages = getAgeGroup(team as any);
    const gender = getAllowedGender(team as any);
    const metadata = [ages, gender].filter(Boolean).join(' · ');
    const rawTeam = team as Team & { isDraft?: boolean; status?: string };
    const isDraft = rawTeam.isDraft === true || String(rawTeam.status || '').toUpperCase() === 'DRAFT';

    return (
      <span className="eSchoolEventAddExistingTeam__teamOption">
        <span className="eSchoolEventAddExistingTeam__teamOptionTop">
          <strong className="eSchoolEventAddExistingTeam__teamOptionName">{team.name}</strong>
          {isDraft && <span className="eSchoolEventAddExistingTeam__teamOptionDraft">Draft</span>}
        </span>
        {metadata && <span className="eSchoolEventAddExistingTeam__teamOptionMeta">{metadata}</span>}
      </span>
    );
  };

  // ─── Build team object for parent ─────────────────────────────────────────

  buildTeam(): any {
    const { selectedTeam, itemsAccumulator } = this.state;
    const players = itemsAccumulator.map(p => ({ userId: p.id, permissionId: p.permissionId }));
    return {
      ...selectedTeam,
      cloneOf: selectedTeam.id,
      teamType: TEAM_TYPE.CLONE,
      players,
      __playersSnapshot: itemsAccumulator.map(player => ({ ...player }))
    };
  }

  // ─── Parent callbacks ─────────────────────────────────────────────────────

  onAddExistingTeamButtonClick = () => {
    const { selectedTeam } = this.state;
    const { onAddExistingTeamClick, currentEvent, teams } = this.props;
    if (!selectedTeam) {
      this.setState({ isError: true });
      return;
    }
    const team = this.buildTeam();
    const nextHouse = isHousesEvent(currentEvent)
      ? currentEvent.houses.find(house => [...teams, team].every(existingTeam => existingTeam.houseId !== house.id))
      : undefined;
    const nextHouseId = nextHouse ? nextHouse.id : this.state.houseId;
    this.setState({
      houseId: nextHouseId,
      selectedTeam: undefined,
      items: [],
      itemsFilters: this.getDefaultItemsFilter(nextHouseId),
      itemsAccumulator: [],
      itemsAccumulatorInitial: [],
      itemsAddBuffer: [],
      itemsRemoveBuffer: []
    });
    onAddExistingTeamClick(team);
  };

  onAddNewTeamButtonClick = () => {
    const { selectedTeam } = this.state;
    const { onAddNewTeamClick } = this.props;
    if (!selectedTeam) {
      this.setState({ isError: true });
      return;
    }
    onAddNewTeamClick(this.buildTeam());
  };

  onSaveButtonClick = (isCreateAnotherEvent: boolean) => {
    const { onSaveClick } = this.props;
    onSaveClick(null, isCreateAnotherEvent);
  };

  onCreateEventClick = () => {
    const { selectedTeam } = this.state;
    const { currentEvent, onSaveClick, teams } = this.props;
    const isDirectSingleTeam = !currentEvent.multiparty && isInterSchoolsEvent(currentEvent);

    if (isDirectSingleTeam && selectedTeam && teams.length === 0) {
      onSaveClick(this.buildTeam(), false);
      return;
    }

    onSaveClick(null, false);
  };

  renderDsIcon(path: string, size = 20): React.ReactNode {
    return (
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
        <path d={path} />
      </svg>
    );
  }

  getSnapshotBeforeUpdate(prevProps: Props, prevState: State): number | null {
    if (!prevState.isLoading && this.state.isLoading && typeof window !== 'undefined') {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }
    return null;
  }

  componentDidUpdate(prevProps: Props, prevState: State, snapshot: number | null) {
    if (snapshot !== null) {
      this.scrollPositionBeforeLoading = snapshot;
    }

    if (prevState.isLoading && !this.state.isLoading && this.scrollPositionBeforeLoading !== null) {
      const scrollTop = this.scrollPositionBeforeLoading;
      this.scrollPositionBeforeLoading = null;
      requestAnimationFrame(() => window.scrollTo({ top: scrollTop, behavior: 'auto' }));
    }
  }

  onRemoveAddedTeamClick = (event: React.MouseEvent<HTMLButtonElement>, teamIndex: number) => {
    event.stopPropagation();
    this.props.onRemoveTeamClick(teamIndex);
  };

  renderTeamsInFixture(): React.ReactNode {
    const { teams } = this.props;
    if (!teams.length) return null;

    return (
      <section className="eSchoolEventAddExistingTeam__teamsCard" data-help-anchor="event-participants-added-teams">
        <div className="eSchoolEventAddExistingTeam__teamsHead">
          <span className="eSchoolEventAddExistingTeam__teamsIcon">
            {this.renderDsIcon(
              'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
              18
            )}
          </span>
          <div>
            <div className="eSchoolEventAddExistingTeam__teamsTitle">Teams in this fixture</div>
            <div className="eSchoolEventAddExistingTeam__subtitle">{teams.length} added</div>
          </div>
        </div>
        <div className="eSchoolEventAddExistingTeam__teamsList">
          {teams.map((team, index) => {
            const playersCount = Number(propz.get(team, ['players', 'length'], 0));
            return (
              <div className="eSchoolEventAddExistingTeam__teamRow" key={`${team.name}-${index}`}>
                <span className="eSchoolEventAddExistingTeam__teamIcon">
                  {this.renderDsIcon('M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 17)}
                </span>
                <div className="eSchoolEventAddExistingTeam__teamMain">
                  <div className="eSchoolEventAddExistingTeam__teamName">{team.name}</div>
                  <div className="eSchoolEventAddExistingTeam__teamMeta">
                    {playersCount} player{playersCount === 1 ? '' : 's'} | existing team
                  </div>
                </div>
                <button
                  type="button"
                  className="eSchoolEventAddExistingTeam__teamRemove"
                  aria-label={`Remove ${team.name}`}
                  onClick={event => this.onRemoveAddedTeamClick(event, index)}
                >
                  {this.renderDsIcon('M18 6 6 18M6 6l12 12', 15)}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // ─── Add / Remove ─────────────────────────────────────────────────────────

  onAddClick = () => {
    const { itemsSelected, itemsAddBuffer, itemsAccumulatorInitial, itemsAccumulator, itemCurrentPage } = this.state;
    const nextAddBuffer = [...itemsAddBuffer, ...itemsSelected];
    const existingIds = new Set(itemsAccumulator.map(x => String(x.id)));
    const nextAccumulator = [...itemsAccumulator, ...itemsSelected.filter(x => !existingIds.has(String(x.id)))];
    this.setState({ isLoading: true });
    this.fetchStudents(itemCurrentPage, itemsAccumulatorInitial, nextAddBuffer).then(([students, count]) => {
      this.setState({
        items: this.normalizeStudents(students),
        itemsCount: count.count,
        itemsAddBuffer: nextAddBuffer,
        itemsAccumulator: nextAccumulator,
        itemsAccumulatorCount: nextAccumulator.length,
        itemsSelected: [],
        itemsAccumulatorSelected: [],
        isSelectAllItemsChecked: false,
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  onRemoveClick = () => {
    const {
      itemsAccumulatorSelected,
      itemsAddBuffer,
      itemsRemoveBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage
    } = this.state;
    const selectedIds = new Set(itemsAccumulatorSelected.map(x => String(x.id)));
    const addBufferIds = new Set(itemsAddBuffer.map(x => String(x.id)));
    const nextAddBuffer = itemsAddBuffer.filter(x => !selectedIds.has(String(x.id)));
    const existingParticipants = itemsAccumulatorSelected.filter(x => !addBufferIds.has(String(x.id)));
    const existingRemoveIds = new Set(itemsRemoveBuffer.map(x => String(x.id)));
    const nextRemoveBuffer = [
      ...itemsRemoveBuffer,
      ...existingParticipants.filter(x => !existingRemoveIds.has(String(x.id)))
    ];
    const nextAccumInitial = itemsAccumulatorInitial.filter(
      p => !nextRemoveBuffer.some(r => String(r.id) === String(p.id))
    );
    const nextAccumulator = itemsAccumulator.filter(x => !selectedIds.has(String(x.id)));
    this.setState({ isLoading: true });
    this.fetchStudents(itemCurrentPage, nextAccumInitial, nextAddBuffer).then(([students, count]) => {
      this.setState({
        items: this.normalizeStudents(students),
        itemsCount: count.count,
        itemsAddBuffer: nextAddBuffer,
        itemsRemoveBuffer: nextRemoveBuffer,
        itemsAccumulatorInitial: nextAccumInitial,
        itemsAccumulator: nextAccumulator,
        itemsAccumulatorCount: nextAccumulator.length,
        itemsSelected: [],
        itemsAccumulatorSelected: [],
        isSelectAllItemsChecked: false,
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  // ─── Click / Select all ───────────────────────────────────────────────────

  onItemClick = (index: number) => {
    const { items, itemsSelected } = this.state;
    const item = items[index];
    const idx = itemsSelected.findIndex(x => x.id === item.id);
    const next = [...itemsSelected];
    idx !== -1 ? next.splice(idx, 1) : next.push(item);
    this.setState({ itemsSelected: next, itemsAccumulatorSelected: [] });
  };

  onItemAccumulatorClick = (index: number) => {
    const { itemsAccumulator, itemsAccumulatorSelected } = this.state;
    const item = itemsAccumulator[index];
    const idx = itemsAccumulatorSelected.findIndex(x => x.id === item.id);
    const next = [...itemsAccumulatorSelected];
    idx !== -1 ? next.splice(idx, 1) : next.push(item);
    this.setState({ itemsAccumulatorSelected: next, itemsSelected: [] });
  };

  onSelectAllItemsClick = () => {
    const { itemsAccumulatorInitial, itemsAddBuffer } = this.state;
    this.setState({ isLoading: true });
    this.fetchStudents(1, itemsAccumulatorInitial, itemsAddBuffer, true).then(([students]) => {
      const normalizedStudents = this.normalizeStudents(students);
      const selectedIds = new Set(this.state.itemsSelected.map(item => String(item.id)));
      this.setState({
        itemsSelected: [
          ...this.state.itemsSelected,
          ...normalizedStudents.filter(item => !selectedIds.has(String(item.id)))
        ],
        isSelectAllItemsChecked: true,
        isLoading: false
      });
    });
  };
  onSelectAllItemsOnPageClick = () =>
    this.setState(s => {
      const selectedIds = new Set(s.itemsSelected.map(item => String(item.id)));
      return {
        itemsSelected: [...s.itemsSelected, ...s.items.filter(item => !selectedIds.has(String(item.id)))],
        isSelectAllItemsChecked: true
      };
    });
  onUnselectAllItemsClick = () => this.setState({ itemsSelected: [], isSelectAllItemsChecked: false });
  onSelectAllItemsAccumulatorClick = () =>
    this.setState(s => ({ itemsAccumulatorSelected: s.itemsAccumulator, isSelectAllItemsAccumulatorChecked: true }));
  onSelectAllItemsAccumulatorOnPageClick = () =>
    this.setState(s => ({ itemsAccumulatorSelected: s.itemsAccumulator, isSelectAllItemsAccumulatorChecked: true }));
  onUnselectAllItemsAccumulatorClick = () =>
    this.setState({ itemsAccumulatorSelected: [], isSelectAllItemsAccumulatorChecked: false });

  // ─── Pagination ───────────────────────────────────────────────────────────

  itemsSetCurrentPageParams = (page: number) => {
    const { itemsAccumulatorInitial, itemsAddBuffer } = this.state;
    this.setState({ isLoading: true, itemCurrentPage: page });
    this.fetchStudents(page, itemsAccumulatorInitial, itemsAddBuffer).then(([students, count]) => {
      this.setState({ items: this.normalizeStudents(students), itemsCount: count.count, isLoading: false });
    });
  };

  // ─── Filters ──────────────────────────────────────────────────────────────

  onItemsFilterChange = (event: any, field: string) => {
    const value = event.target ? event.target.value : event;
    this.setState(s => ({ itemsFilters: { ...s.itemsFilters, [field]: value } }));
  };
  onItemsFilterClick = () => this.setState(s => ({ isShowItemsFilter: !s.isShowItemsFilter }));

  itemsOnApplyFilterClick = (isSilent = false) => {
    const { itemsAccumulatorInitial, itemsAddBuffer } = this.state;
    const requestId = ++this.itemsSearchRequestId;
    if (isSilent) {
      this.setState({ isItemsLoading: true, itemCurrentPage: 1 });
    } else {
      this.setState({ isLoading: true, itemCurrentPage: 1 });
    }
    this.fetchStudents(1, itemsAccumulatorInitial, itemsAddBuffer).then(([students, count]) => {
      if (requestId !== this.itemsSearchRequestId) return;
      this.setState({
        items: this.normalizeStudents(students),
        itemsCount: count.count,
        isItemsFiltered: true,
        isLoading: isSilent ? this.state.isLoading : false,
        isItemsLoading: false
      });
    });
  };

  onItemsSearchChange = (itemsSearch: string) => {
    this.setState({ itemsSearch, isItemsLoading: true }, () => this.itemsOnApplyFilterClick(true));
  };

  itemsOnClearFilterClick = () => {
    const defaultFilter = this.getDefaultItemsFilter();
    this.setState({ isLoading: true, itemsFilters: defaultFilter, itemCurrentPage: 1 }, () => {
      const { itemsAccumulatorInitial, itemsAddBuffer } = this.state;
      this.fetchStudents(1, itemsAccumulatorInitial, itemsAddBuffer).then(([students, count]) => {
        this.setState({
          items: this.normalizeStudents(students),
          itemsCount: count.count,
          isItemsFiltered: false,
          isLoading: false
        });
      });
    });
  };

  onItemsAccumulatorFilterChange = (event: any, field: string) => {
    const value = event.target ? event.target.value : event;
    this.setState(s => ({ itemsAccumulatorFilters: { ...s.itemsAccumulatorFilters, [field]: value } }));
  };
  onItemsAccumulatorFilterClick = () =>
    this.setState(s => ({ isShowItemsAccumulatorFilter: !s.isShowItemsAccumulatorFilter }));
  itemsAccumulatorOnApplyFilterClick = () => this.setState({ isItemsAccumulatorFiltered: true });
  itemsAccumulatorOnClearFilterClick = () => {
    this.setState({
      itemsAccumulatorFilters: getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS),
      isItemsAccumulatorFiltered: false
    });
  };

  onSortItemsClick = (sortField: string) => {
    const {
      sortItemsDirection,
      sortItemsColumnsName,
      itemCurrentPage,
      itemsAccumulatorInitial,
      itemsAddBuffer
    } = this.state;
    const order = getOrder(sortField, sortItemsDirection, sortItemsColumnsName) as TABLE_SORT_DIRECTION;
    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulatorInitial, itemsAddBuffer);
    filter.order = `${sortField} ${order}`;
    const { user } = this.props;
    const method = getStudentsMethod(user);

    this.setState({ isLoading: true });
    Promise.all([method(user, filter), getSchoolStudentsWithClashesCount(user, filter)]).then(
      ([students, studentsCount]) => {
        this.setState({
          items: this.normalizeStudents(students),
          itemsCount: studentsCount.count,
          itemCurrentPage: 1,
          itemsSelected: [],
          itemsAccumulatorSelected: [],
          sortItemsColumnsName: sortField,
          sortItemsDirection: order,
          isLoading: false
        });
      }
    );
  };

  onReplaceItemsFilters = (itemsFilters: any) => {
    this.setState({ itemsFilters }, this.itemsOnApplyFilterClick);
  };

  onSortItemsAccumulatorClick = (sortField: string) => {
    const { itemsAccumulator, sortItemsAccumulatorDirection, sortItemsAccumulatorColumnsName } = this.state;
    const order = getOrder(
      sortField,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName
    ) as TABLE_SORT_DIRECTION;
    const direction = String(order).toUpperCase() === 'DESC' ? -1 : 1;
    const itemsSorted = [...itemsAccumulator].sort(
      (a, b) =>
        String(a[sortField] || '').localeCompare(String(b[sortField] || ''), undefined, { sensitivity: 'base' }) *
        direction
    );

    this.setState({
      itemsAccumulator: itemsSorted,
      itemsAccumulatorSelected: [],
      sortItemsAccumulatorColumnsName: sortField,
      sortItemsAccumulatorDirection: order
    });
  };

  // ─── Clash modal ──────────────────────────────────────────────────────────

  onClashClick = (player: any) => {
    const { user } = this.props;
    const { clashes, firstName, lastName } = player;
    const { fullClashEvents = [], intervalClashEvents = [] } = clashes || {};
    const fullIds = fullClashEvents.map((e: any) => e._id);
    const intervalIds = intervalClashEvents.map((e: any) => e._id);
    const allIds = Lazy([...fullIds, ...intervalIds])
      .uniq()
      .toArray();
    this.setState({ isLoading: true });
    const eventsPromises: Promise<SchoolEvent>[] = allIds.map((id: string) => getSchoolEvent(user, id));
    BPromise.all(eventsPromises).then((events: SchoolEvent[]) => {
      const names = events
        .map((ev, i) => `${i + 1}. ${getClashEventDisplayTitle(ev, user)} ${getEventClash(ev, fullIds, intervalIds)}`)
        .join('\n ');
      this.setState({
        isLoading: false,
        playerEventsModalText: `${firstName} ${lastName} is participating in:\n${names}`,
        isPlayerEventsModalOpen: true
      });
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    const {
      isLoading,
      isError,
      forms,
      houses,
      selectedTeam,
      items,
      itemsCount,
      itemsSelected,
      itemsFilters,
      isShowItemsFilter,
      isItemsFiltered,
      isSelectAllItemsChecked,
      sortItemsDirection,
      sortItemsColumnsName,
      itemCurrentPage,
      itemsAccumulator,
      itemsAccumulatorCount,
      itemsAccumulatorSelected,
      itemsAccumulatorFilters,
      isShowItemsAccumulatorFilter,
      isItemsAccumulatorFiltered,
      isSelectAllItemsAccumulatorChecked,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName,
      itemsAddBuffer,
      itemsRemoveBuffer,
      isPlayerEventsModalOpen,
      itemsHiddenFields,
      itemsAccumulatorHiddenFields
    } = this.state;

    const { isSaveButtonDisabled, isAddTeamButtonDisabled, minTeams, maxTeams, teams, user, goBack } = this.props;

    if (isLoading && items.length === 0 && itemsAccumulator.length === 0) return <Loader />;

    const { activeSchool } = user;
    const { ageGroupsNaming, isShowStudentEventClashes } = activeSchool;

    const allItemsColumns = isShowStudentEventClashes ? ITEMS_COLUMNS_WITH_CLASHES : ITEMS_COLUMNS;
    const allAccumulatorColumns = isShowStudentEventClashes
      ? ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES
      : ITEMS_ACCUMULATOR_COLUMNS;
    const visibleItemsColumns = allItemsColumns.filter(col => !itemsHiddenFields.has(col.field));
    const visibleAccumulatorColumns = allAccumulatorColumns.filter(col => !itemsAccumulatorHiddenFields.has(col.field));

    const itemsFiltersOptions = {
      ages: getSelectOptionForAge(forms, ageGroupsNaming),
      gender: getSelectOptionForGender(),
      forms: getSelectOptionForForms(forms),
      houses: getSelectOptionForHouses(houses)
    };
    const eventGenderPreset = getUserGenderArrayConvertedFromEventGender(this.props.currentEvent.gender);
    const itemsPresetFilters = {
      ages: (this.props.currentEvent.ages || []).map(age => String(age)),
      gender: eventGenderPreset.length === 1 ? eventGenderPreset.map(String) : []
    };

    const classes = isPlayerEventsModalOpen ? 'eSchoolEventAddExistingTeam modal-open' : 'eSchoolEventAddExistingTeam';
    const isDirectSingleTeam = !this.props.currentEvent.multiparty && isInterSchoolsEvent(this.props.currentEvent);
    const isCurrentTeamPending = Boolean(selectedTeam);
    const isCurrentTeamReady = Boolean(selectedTeam) && !isAddTeamButtonDisabled;
    const hasUnstagedTeam = !isDirectSingleTeam && isCurrentTeamPending;
    const baseCanCreateEvent = isDirectSingleTeam
      ? Boolean(selectedTeam) || teams.length > 0
      : !isSaveButtonDisabled && teams.length > 0;
    const canCreateEvent = baseCanCreateEvent && !hasUnstagedTeam;
    const actionStatus = isDirectSingleTeam
      ? selectedTeam
        ? `${itemsAccumulatorCount} ${itemsAccumulatorCount === 1 ? 'player' : 'players'} in selected team`
        : `${teams.length} team${teams.length === 1 ? '' : 's'} ready`
      : hasUnstagedTeam
      ? 'Add the selected team before creating'
      : teams.length < minTeams
      ? `${teams.length} of ${minTeams} required teams added`
      : `${teams.length} of maximum ${maxTeams} teams added`;
    const selectedHouse =
      houses.find(house => house.id === this.state.houseId) ||
      this.props.currentEvent.houses.find(house => house.id === this.state.houseId);

    return (
      <div className={classes}>
        <SchoolEventParticipantsPageHelp
          mode="existing"
          user={user}
          currentEvent={this.props.currentEvent}
          availableCount={itemsCount}
          selectedCount={itemsAccumulatorCount}
          teamsCount={teams.length}
          minTeams={minTeams}
          maxTeams={maxTeams}
          isCreateEnabled={canCreateEvent}
          isAddTeamDisabled={isAddTeamButtonDisabled}
          isCurrentTeamPending={isCurrentTeamPending}
          isCurrentTeamReady={isCurrentTeamReady}
          selectedTeamName={selectedTeam ? getNameAndAges(selectedTeam) : ''}
          houseName={selectedHouse ? selectedHouse.name : ''}
        />
        <ClashEventsModal
          isOpen={isPlayerEventsModalOpen}
          text={this.state.playerEventsModalText}
          onClose={() => this.setState({ isPlayerEventsModalOpen: false })}
        />

        <section className="eSchoolEventAddExistingTeam__buildCard">
          <div className="eSchoolEventAddExistingTeam__buildHead">
            <span className="eSchoolEventAddExistingTeam__buildIcon">
              {this.renderDsIcon('M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 18)}
            </span>
            <div className="eSchoolEventAddExistingTeam__headCopy">
              <h3 className="eSchoolEventAddExistingTeam__title">Choose an existing team</h3>
              <div className="eSchoolEventAddExistingTeam__subtitle">
                Select a saved school team, then review and adjust its players.
              </div>
            </div>
            {this.props.participantsModeSwitch}
          </div>
          <div className="eSchoolEventAddExistingTeam__divider" />
          <div className="eSchoolEventAddExistingTeam__buildBody">
            <div className="eSchoolEventAddExistingTeam__teamPicker" data-help-anchor="event-participants-team-picker">
              {isHousesEvent(this.props.currentEvent) && (
                <div className="eSchoolEventAddExistingTeam__houseField" data-help-anchor="event-participants-house">
                  <label className="eSchoolEventAddExistingTeam__label">
                    House <span className="eSchoolEventAddExistingTeam__required">*</span>
                  </label>
                  <div className="eSchoolEventAddExistingTeam__houseControl">
                    <span className="eSchoolEventAddExistingTeam__houseControlIcon" aria-hidden="true">
                      {selectedHouse && selectedHouse.pic ? (
                        <img className="eSchoolEventAddExistingTeam__houseLogo" src={selectedHouse.pic} alt="" />
                      ) : (
                        <span className="eSchoolEventAddExistingTeam__houseLogoFallback">
                          {selectedHouse && selectedHouse.name ? selectedHouse.name.charAt(0) : 'H'}
                        </span>
                      )}
                    </span>
                    <select
                      className="form-control eSchoolEventAddExistingTeam__houseSelect"
                      value={this.state.houseId}
                      onChange={this.onHouseChange}
                    >
                      {this.props.currentEvent.houses
                        .filter(house => teams.every(team => team.houseId !== house.id))
                        .map(house => (
                          <option key={house.id} value={house.id}>
                            {house.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              )}
              <label className="eSchoolEventAddExistingTeam__label">
                Existing team <span className="eSchoolEventAddExistingTeam__required">*</span>
              </label>
              <Autocomplete
                searchFunction={this.getSchoolTeams}
                getElementTitle={getNameAndAges}
                onSelect={this.onSelectTeam}
                defaultItem={selectedTeam}
                placeholder={'Search teams by name'}
                customClass="eSchoolEventAddExistingTeam__autocomplete"
                disabled={isAddTeamButtonDisabled && !isDirectSingleTeam}
                renderItem={this.renderTeamOption}
              />
              <div className="eSchoolEventAddExistingTeam__hint">
                {isAddTeamButtonDisabled && !isDirectSingleTeam
                  ? `The maximum of ${maxTeams} ${
                      maxTeams === 1 ? 'team has' : 'teams have'
                    } been added. Remove a staged team before choosing another.`
                  : "Results are matched to the event's sport, age group and gender."}
              </div>
              {isError && <div className="eSchoolEventAddExistingTeam__fieldError">Please select a team.</div>}
            </div>

            {selectedTeam && (
              <div className="eSchoolEventAddExistingTeam__selectedTeam">
                <div className="eSchoolEventAddExistingTeam__selectedHead">
                  <div>
                    <div className="eSchoolEventAddExistingTeam__selectedLabel">Selected team</div>
                    <div className="eSchoolEventAddExistingTeam__selectedName">{getNameAndAges(selectedTeam)}</div>
                  </div>
                  <div className="eSchoolEventAddExistingTeam__selectedCount">
                    {itemsAccumulatorCount} player{itemsAccumulatorCount === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="eSchoolEventAddExistingTeam__editorWrap" data-help-anchor="event-participants-students">
                  <TwoPanelEditor
                    isSourceFirst
                    itemsPresetFilters={itemsPresetFilters}
                    onReplaceItemsFilters={this.onReplaceItemsFilters}
                    onItemsSearchChange={this.onItemsSearchChange}
                    itemsSearchValue={this.state.itemsSearch}
                    isItemsLoading={this.state.isItemsLoading}
                    itemsAccumulator={itemsAccumulator}
                    itemsAccumulatorTitle={'Selected students'}
                    itemsAccumulatorColumns={visibleAccumulatorColumns}
                    onItemAccumulatorClick={this.onItemAccumulatorClick}
                    itemsAccumulatorSelected={itemsAccumulatorSelected}
                    onItemsAccumulatorFilterChange={this.onItemsAccumulatorFilterChange}
                    itemsAccumulatorFilters={itemsAccumulatorFilters}
                    itemsAccumulatorFiltersOptions={{}}
                    isShowItemsAccumulatorFilter={isShowItemsAccumulatorFilter}
                    isItemsAccumulatorFiltered={isItemsAccumulatorFiltered}
                    onItemsAccumulatorFilterClick={this.onItemsAccumulatorFilterClick}
                    itemsAccumulatorCount={itemsAccumulatorCount}
                    itemAccumulatorCurrentPage={1}
                    onSelectAllItemsAccumulatorClick={this.onSelectAllItemsAccumulatorClick}
                    onSelectAllItemsAccumulatorOnPageClick={this.onSelectAllItemsAccumulatorOnPageClick}
                    onUnselectAllItemsAccumulatorClick={this.onUnselectAllItemsAccumulatorClick}
                    isSelectAllItemsAccumulatorChecked={isSelectAllItemsAccumulatorChecked}
                    itemsAccumulatorOnApplyFilterClick={this.itemsAccumulatorOnApplyFilterClick}
                    itemsAccumulatorOnClearFilterClick={this.itemsAccumulatorOnClearFilterClick}
                    sortItemsAccumulatorDirection={sortItemsAccumulatorDirection}
                    sortItemsAccumulatorColumnsName={sortItemsAccumulatorColumnsName}
                    onSortItemsAccumulatorClick={this.onSortItemsAccumulatorClick}
                    items={items}
                    itemsTitle={'Available students'}
                    itemsColumns={visibleItemsColumns}
                    itemsSelected={itemsSelected}
                    onItemClick={this.onItemClick}
                    onItemsFilterChange={this.onItemsFilterChange}
                    itemsFilters={itemsFilters}
                    itemsFiltersOptions={itemsFiltersOptions}
                    isShowItemsFilter={isShowItemsFilter}
                    isItemsFiltered={isItemsFiltered}
                    onItemsFilterClick={this.onItemsFilterClick}
                    itemsCount={itemsCount}
                    itemCurrentPage={itemCurrentPage}
                    itemsSetCurrentPageParams={this.itemsSetCurrentPageParams}
                    onSelectAllItemsClick={this.onSelectAllItemsClick}
                    onSelectAllItemsOnPageClick={this.onSelectAllItemsOnPageClick}
                    onUnselectAllItemsClick={this.onUnselectAllItemsClick}
                    isSelectAllItemsChecked={isSelectAllItemsChecked}
                    itemsOnApplyFilterClick={this.itemsOnApplyFilterClick}
                    itemsOnClearFilterClick={this.itemsOnClearFilterClick}
                    sortItemsDirection={sortItemsDirection}
                    sortItemsColumnsName={sortItemsColumnsName}
                    onSortItemsClick={this.onSortItemsClick}
                    onAddClick={this.onAddClick}
                    onRemoveClick={this.onRemoveClick}
                    itemsAddBuffer={itemsAddBuffer}
                    itemsRemoveBuffer={itemsRemoveBuffer}
                    onCellItemsClick={this.onClashClick}
                    onCellItemsAccumulatorClick={this.onClashClick}
                    itemsAllColumns={allItemsColumns}
                    itemsHiddenFields={itemsHiddenFields}
                    onToggleItemsColumn={this.onToggleItemsColumn}
                    isItemsColumnToggleable={this.isColumnToggleable}
                    itemsAccumulatorAllColumns={allAccumulatorColumns}
                    itemsAccumulatorHiddenFields={itemsAccumulatorHiddenFields}
                    onToggleItemsAccumulatorColumn={this.onToggleItemsAccumulatorColumn}
                    isItemsAccumulatorColumnToggleable={this.isColumnToggleable}
                  />
                </div>
                {!isDirectSingleTeam && (
                  <div
                    className="eSchoolEventAddExistingTeam__addTeamRow"
                    data-help-anchor="event-participants-stage-team"
                  >
                    <button
                      type="button"
                      className="eSchoolEventAddExistingTeam__btnAddTeam"
                      onClick={this.onAddExistingTeamButtonClick}
                      disabled={isAddTeamButtonDisabled}
                    >
                      {this.renderDsIcon('M12 5v14M5 12h14', 15)}
                      Add team to fixture
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {this.renderTeamsInFixture()}

        <div className="eSchoolEventAddExistingTeam__actions" data-help-anchor="event-participants-actions">
          <button type="button" className="eSchoolEventAddExistingTeam__btnBack" onClick={goBack}>
            {this.renderDsIcon('M19 12H5M11 18l-6-6 6-6', 15)}
            Back to details
          </button>
          <span className="eSchoolEventAddExistingTeam__actionsSpacer" />
          <span className="eSchoolEventAddExistingTeam__teamsCount">{actionStatus}</span>
          <button
            type="button"
            className="eSchoolEventAddExistingTeam__btnCreate"
            disabled={!canCreateEvent}
            onClick={this.onCreateEventClick}
          >
            {this.renderDsIcon('M20 6 9 17l-5-5', 15)}
            Create event
          </button>
        </div>
      </div>
    );
  }
}
